import * as T from '../utilities/frontendTypes';
import { Address } from '../Components/Form/AddressFields';
import { apiGet, apiGetBlob, apiPost, apiPut, apiDelete } from './apiClient';

export async function getSowRequests(): Promise<T.SowRequestResponse> {
  return apiGet<T.SowRequestResponse>("/api/providers/get-sow-requests");
}

export async function getSowDetails(sowId: string): Promise<T.CreateSowRequestResponse> {
  return apiGet<T.CreateSowRequestResponse>(`/api/providers/get-sow-details/${sowId}`);
}

export async function getSowCaps(): Promise<T.SowCapsResponse> {
  return apiGet<T.SowCapsResponse>("/api/providers/sow-caps");
}

export async function serviceProviderSubmitSow(sowDetails: T.SowDetailToBackend): Promise<T.HTTPResponse> {
  return apiPut<T.HTTPResponse>("/api/providers/submit-sow", sowDetails);
}


export async function serviceProviderSaveProgressSow(sowDetails: T.SowDetailToBackend): Promise<T.HTTPResponse> {
  return apiPut<T.HTTPResponse>("/api/providers/save-sow", sowDetails);
}

export async function getProviderDashboard(): Promise<{success: boolean, sowArray: {companyName: string, actionItem: string, lastUpdated: Date, tier: string, contactPerson: string, contactEmail: string, sowId: string, revisionInvoiceId?: string}[], name: string}> {
  return apiGet<{success: boolean, sowArray: {companyName: string, actionItem: string, lastUpdated: Date, tier: string, contactPerson: string, contactEmail: string, sowId: string, revisionInvoiceId?: string}[], name: string}>("/api/providers/dashboard");
}

export async function getDownloadUrl(docKey: string): Promise<{success: boolean, url: string }> {
  return apiGet<{success: boolean, url: string}>(`/api/providers/get-pdf/${docKey}`);
}

export async function getPdfBytes(docKey: string): Promise<ArrayBuffer> {
  return apiGetBlob(`/api/providers/get-pdf-bytes/${docKey}`);
}

import type { InPortalSignerInfo } from "@eip/shared/types/sow.types";

export async function getSpSigningInfo(sowId: string): Promise<InPortalSignerInfo> {
  return apiGet<InPortalSignerInfo>(`/api/providers/sow/${sowId}/signing-info`);
}

export async function submitSpSignature(sowId: string, signatureBase64: string, consent: boolean, printedName: string): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>(`/api/providers/sow/${sowId}/sign`, { signatureBase64, consent, printedName });
}

export async function getInvoicesData(): Promise<{success: boolean, invoiceArray: [{company: string, status: string, type: string, date: Date, sowId: string}]}> {
  return apiGet<{success: boolean, invoiceArray: [{company: string, status: string, type: string, date: Date, sowId: string}]}>("/api/providers/get-invoices-data");
}

export async function getSowDetailsForInvoice(sowId: string): Promise<{sow: T.Statement, coinvest: number}> {
  return apiGet<{sow: T.Statement, coinvest: number}>(`/api/providers/get-create-invoice/${sowId}`);
}

export async function checkRetainerInvoiceEligibility(sowId: string): Promise<T.HTTPResponse> {
  return apiGet<T.HTTPResponse>(`/api/providers/retainer-invoice-eligibility/${sowId}`);
}

export async function newRetainerInvoice(sowId: string, totalArray: number[], attestations: { accurateInfo: boolean }, memo?: string): Promise<T.HTTPResponse> {
  return apiPost<T.HTTPResponse>("/api/providers/new-retainer-invoice", { sowId, totalArray, attestations, ...(memo ? { memo } : {}) });
}

export async function newFinalInvoice(sowId: string, sowItems: T.SowItem[], preTax: number, tax: number, existingRetainerInvoiceTotal: number, memo?: string): Promise<T.HTTPResponse> {
  return apiPost<T.HTTPResponse>("/api/providers/new-final-invoice", { sowId, sowItems, preTax, tax, existingRetainerInvoiceTotal, ...(memo ? { memo } : {}) });
}

export async function finalInvoiceSubmit(invoiceId: string, attestations: { accurateInfo: boolean; deliverablesConfirmed: boolean }): Promise<T.HTTPResponse> {
  return apiPost<T.HTTPResponse>("/api/providers/final-invoice-submit", { invoiceId, attestations });
}

export type ResubmitRetainerPayload = {
  totalArray: number[];
  memo?: string;
  attestations: { accurateInfo: true };
};

export type ResubmitFinalPayload = {
  sowItems: T.SowItem[];
  preTax: number;
  tax: number;
  existingRetainerInvoiceTotal: number;
  memo?: string;
  attestations: { accurateInfo: true; deliverablesConfirmed: true };
};

export async function resubmitInvoice(
  invoiceId: string,
  payload: ResubmitRetainerPayload | ResubmitFinalPayload
): Promise<T.HTTPResponse> {
  return apiPut<T.HTTPResponse>(`/api/providers/resubmit-invoice/${invoiceId}`, payload);
}

export async function getFirmProfile(): Promise<{success: boolean, firm: T.ServiceProviderFirm}> {
  return apiGet<{success: boolean, firm: T.ServiceProviderFirm}>("/api/providers/firm-profile");
}

export async function updateFirmProfile(firmData: Record<string, unknown>): Promise<T.HTTPResponse> {
  return apiPut<T.HTTPResponse>("/api/providers/firm-profile", firmData);
}

export async function getPractitioners(): Promise<{success: boolean, practitioners: T.Practitioner[]}> {
  return apiGet<{success: boolean, practitioners: T.Practitioner[]}>("/api/providers/practitioners");
}

export async function addPractitioner(practitionerData: {name: string, email: string}): Promise<T.HTTPResponse> {
  return apiPost<T.HTTPResponse>("/api/providers/practitioners", practitionerData);
}

export async function updatePractitioner(practitionerId: string, practitionerData: {name: string, email: string}): Promise<T.HTTPResponse> {
  return apiPut<T.HTTPResponse>(`/api/providers/practitioners/${practitionerId}`, practitionerData);
}

export async function deletePractitioner(practitionerId: string): Promise<T.HTTPResponse> {
  return apiDelete<T.HTTPResponse>(`/api/providers/practitioners/${practitionerId}`);
}

export async function getInvoiceDetailsAsync(invoiceId: string): Promise<{success: boolean, invoice: T.Invoice, otherProjectFinal: string | null, otherProjectRetainer: string | null,  associatedFinal: string | null, associatedRetainer: string | null, application: T.ApplicationDetails, sow: T.Statement, coPayPercentage: number, associatedRetainerInvoiceTotal: number, spName: string, spOwnerName: string, address: Address, spHstNumber?: string, spPstNumber?: string}> {
  return apiGet<{success: boolean, invoice: T.Invoice, otherProjectFinal: string | null, otherProjectRetainer: string | null, associatedFinal: string | null, associatedRetainer: string | null, application: T.ApplicationDetails, sow: T.Statement, coPayPercentage: number, associatedRetainerInvoiceTotal: number, spName: string, spOwnerName: string, address: Address, spHstNumber?: string, spPstNumber?: string}>(`/api/providers/get-invoice/${invoiceId}`);
}
